import { useEffect } from "react";
import { ToastContainer } from "react-toastify";

import AppRouting from "@/src/AppRouting";
import {
    ActiveLoginModalContextProvider,
    ShowRegistrationOnboardingContextProvider,
    UserAuthenticationContextProvider,
    ViewportInfoContextProvider,
} from "@/src/contexts";
import { LocationContextProvider } from "@/src/contexts/LocationContext";
import { ModelStackContextProvider } from "@/src/contexts/ModelStackContext";
import { PageContextProvider } from "@/src/contexts/PageContext";
import HashManager from "@/src/hash-manager";
import Layout from "@/src/master/Layout";
import type { IMaster } from "@/src/master/Master";
import SvgSprite from "@/src/master/svg-sprite";
import PageLoadProcessor from "@/src/page-load-processor";
import { browserStatics } from "@/src/shared/BrowserStatic";
import UserContext from "@/src/user/UserContext";
import { UserLanguageType } from "@Components/Modules/Helpers";

const App = (props: IMaster) => {
    browserStatics.language = props.userData.language;
    browserStatics.culture = props.userData.language === UserLanguageType.English ? "en-US" : "pl-PL";
    browserStatics.browserOs = (props.browserOs || "Default").toLocaleUpperCase();
    browserStatics.isMobileApp = props.isMobileApp;
    browserStatics.shouldUserSeeAds = props.shouldUserSeeAds;
    if (props.imageSupport) {
        browserStatics.imageSupport = props.imageSupport;
        browserStatics.imageSupportFormat = `f=${props.imageSupport}`;
    }

    useEffect(() => {
        const clickEvent = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.hasAttribute("href")) {
                return;
            }

            const src = target.getAttribute("href");
            if (src && src.startsWith("javascript:Didomi.")) {
                event.preventDefault();
                event.stopPropagation();

                const didomiCommand = src.replace("javascript:Didomi.", "");

                const didomi = window.Didomi;
                const commands = didomiCommand.split(".");
                const params =
                    commands[commands.length - 1].replaceAll("'", '"').match(/\(((.*))\)/)?.[1] || "";
                let action = didomi;
                for (let cmd of commands) {
                    cmd = cmd.split("(")[0]; // Remove parameters
                    if (action[cmd]) {
                        action = action[cmd];
                    }
                }
                if (typeof action === "function") {
                    action(...JSON.parse("[" + params + "]"));
                }
            }
        };

        window.addEventListener("click", clickEvent);

        return () => window.removeEventListener("click", clickEvent);
    }, []);

    return (
        <ViewportInfoContextProvider device={props.device}>
            <ModelStackContextProvider>
                <ShowRegistrationOnboardingContextProvider>
                    <ActiveLoginModalContextProvider
                        viewModal={props.loginModalView}
                        reCaptchaPublicToken={props.reCaptchaPublicToken}
                    >
                        <LocationContextProvider userLocation={props.location}>
                            <UserAuthenticationContextProvider
                                userContext={props.userData}
                                preventAuthenticationRefresh={props.preventAuthenticationRefresh}
                            >
                                <PageContextProvider {...props.initialData}>
                                    <SvgSprite />
                                    <Layout {...props}>
                                        <AppRouting />
                                    </Layout>
                                    <HashManager />
                                    <PageLoadProcessor />
                                    <UserContext />
                                    <ToastContainer />
                                </PageContextProvider>
                            </UserAuthenticationContextProvider>
                        </LocationContextProvider>
                    </ActiveLoginModalContextProvider>
                </ShowRegistrationOnboardingContextProvider>
            </ModelStackContextProvider>
        </ViewportInfoContextProvider>
    );
};

export default App;
